import store from '../store';

import CacheUtils from './CacheUtils';
import { CONST } from '../constant';
import CommunityMemberApi from '../api/communityMember';

const OLD_CM_DATA_MSEC = 1000 * 60 * 60 * 24 * 7;

export default class App {
  /**
    @param cmid: number,
    @param cid: number,
    @param appParam?: { type: APP_TYPE; id: number; cid: number },
    @param includeInactive?: boolean
    @return : { cm: Cm; requireRefreshAppParam: boolean; noCache: boolean }
   */
  static async toCmForView(cmid, cid, appParam, includeInactive) {
    let cm = await CacheUtils.getCm(cmid);
    if (cm) {
      if (includeInactive) return App.formatCmForView(cm, false, false);
      if (cm.is_friend) {
        if (cm.friend_status == CONST.FRIEND_STATUS.ACTIVE) {
          return App.formatCmForView(cm, false, false);
        }
      } else {
        if (cm.status == CONST.COMMUNTIY_MEMBER_STATUS.ACCEPTED) {
          return App.formatCmForView(cm, false, false);
        }
      }
    }
    if (appParam && (await App.isAppParamAccessile(cid, appParam))) {
      const fm = await CacheUtils.getFriendMap(appParam.type, appParam.id);
      if (fm && fm.map) cm = fm.map[cmid];
      return App.formatCmForView(cm || App.dummyCm(cmid, cid), !fm || fm.t < time() - OLD_CM_DATA_MSEC, false);
    }

    return App.formatCmForView(cm || App.dummyCm(cmid, cid), false, true);
  }

  /*
    @param cm: Cm,
    @param requireRefreshAppParam: boolean,
    @param noCache: boolean
    @return { cm: Cm, requireRefreshAppParam: boolean, noCache: boolean }
   */
  static formatCmForView(cm, requireRefreshAppParam, noCache) {
    if (cm.nickname) return { cm, requireRefreshAppParam, noCache };
    if (cm.guest) {
      cm.nickname = $T('common.role.guest');
      return { cm, requireRefreshAppParam, noCache };
    }
    const canSee = App.canSeeOtherMember(cm.community_id);
    cm.nickname = canSee ? $T('common.member_status.retired') : $T('attributes.other_member');
    if (!canSee) cm.retired = true;
    return { cm, requireRefreshAppParam, noCache };
  }

  /**
    @param cid: number
    @return boolean
   */
  static canSeeOtherMember(cid) {
    const c = store.state.community.communityMap[cid];
    if (!c) return false;
    return c.pm || App.isAdminRole(c.role);
  }

  /**
    Whether the current member may create/share content (posts, events, album images) in this community.
    In a publicity_member=0 ("member-to-member direct interaction OFF") community a general member may not,
    so the TALK share (+) and image-add affordances are hidden for them. The friend community, publicity_member=1
    communities and admins are unrestricted (mirrors the server-side MemberTargetGuard).
    @param cid: number
    @return boolean
   */
  static canShareInCommunity(cid) {
    return App.isFriendCid(cid) || App.canSeeOtherMember(cid);
  }

  /**
    @param role: COMMUNITY_ROLE
    @return boolean
   */
  static isAdminRole(role) {
    return role == CONST.COMMUNITY_ROLE.OWNER || role == CONST.COMMUNITY_ROLE.MANAGER;
  }

  /**
    @param appParam: {
      type: APP_TYPE;
      id: number;
      cid: number;
    }
    @return Promise<{ [key: number]: Cm }>
   */
  static async refreshFriendMap(appParam) {
    const friends = await CommunityMemberApi.getFriends(appParam.type, appParam.id);
    const cms = {};
    for (const f of friends) cms[f.id] = f;
    await CacheUtils.setFriendMap(appParam.type, appParam.id, cms);
    return cms;
  }

  /**
    @param cid: number
    @return Promise<{ groups: Group[]; cms: Cm[]; inactiveCms: Cm[] }>
   */
  static async getGroupsAndCmsFromServer(cid) {
    const result = await CommunityMemberApi.getGroupsAndCommunityMembers(cid, true, true);

    const cms = [];
    const iaCms = [];
    for (const cm of result.communityMembers) {
      if (cm.is_friend) {
        if (cm.friend_status == CONST.FRIEND_STATUS.ACTIVE) {
          cms.push(cm);
        } else {
          iaCms.push(cm);
        }
      } else {
        if (cm.status == CONST.COMMUNTIY_MEMBER_STATUS.ACCEPTED) {
          cms.push(cm);
        } else {
          iaCms.push(cm);
        }
      }
    }

    const mut = time();
    await CacheUtils.setGroups(cid, result.groups, mut);
    await CacheUtils.setCms(cid, cms, mut);
    await CacheUtils.setInactiveCms(cid, iaCms, mut);
    return { groups: result.groups, cms, inactiveCms: iaCms };
  }

  /**
    @param cid: number
    @param appParam: { type: APP_TYPE; id: number }
    @return boolean
   */
  static async isAppParamAccessile(cid, appParam) {
    if (App.isFriendCid(cid)) return true;
    if (appParam.type == CONST.APP_TYPE.POST) {
      const p = await CacheUtils.getPost(appParam.id);
      return (
        !!p &&
        [
          CONST.POST_TYPE.JOIN_ACCEPTANCE,
          CONST.POST_TYPE.JOIN_INVITATION,
          CONST.POST_TYPE.FRIEND_ACCEPTANCE,
          CONST.POST_TYPE.FRIEND_INVITATION,
        ].indexOf(p.type) != -1
      );
    }
    return false;
  }

  /**
    @param cid:number
    @return boolean
   */
  static isFriendCid(cid) {
    return cid == store.state.community.fcid;
  }

  /**
   @param cmid: number
   @param cid: number
   @return Cm
   */
  static dummyCm(cmid, cid) {
    return {
      id: cmid,
      nickname: null,
      community_id: cid,
      guest: false,
      dummy: true,
    };
  }
}
